import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import SegmentedToggle from '../components/SegmentedToggle';
import { Lock } from 'lucide-react';
import DeleteTenantModal from '../components/DeleteTenantModal';
import VatRatesGrid from '../components/VatRatesGrid';
import { updateTenant, dumpTenant, restoreTenant } from '../api/tenants';
import { listAdminTenants } from '../api/admin';
import './TenantSettingsPage.css';

// Modules restaurables (mêmes ids que RESTORE_MODULES côté serveur).
// « Paramètres » = réglages des modules (canton, TVA, 13e/14e, LPP par défaut…).
// L'IDENTITÉ du client (nom, slug, modules vendus, Assistant IA) n'est JAMAIS
// dans la restauration — elle se gère dans Administration uniquement.
const RESTORE_MODULES = [
  ['people', 'People'],
  ['finance', 'Finance'],
  ['rentabilite', 'Rentabilité'],
  ['sales', 'Ventes'],
  ['governance', 'Cockpit & gouvernance'],
  ['settings', 'Paramètres (TVA, canton, salaires…)'],
];

// Données SAISIES À LA MAIN, coûteuses à reconstituer (≠ réel financier,
// rechargeable depuis la comptabilité en quelques minutes). Leur présence dans
// les lignes écrasées élève le niveau d'alerte de la restauration.
// Correspondance par préfixe de table, premier trouvé gagne.
const DELICATE_TABLES = [
  ['hr_people', 'Collaborateurs'],
  ['hr_contracts', 'Contrats'],
  ['hr_annual_extras', 'Extras saisis'],
  ['hr_expense_items', 'Frais saisis'],
  ['payroll_simulation_movements', 'Mouvements de paie'],
  ['tdabc_', 'Rentabilité (coût de production, CCR)'],
  ['sales_', 'Ventes (plans & facturation)'],
  ['treasury_', 'Simulation liquidité'],
  ['pl_line_forecast', 'Prévisionnel P&L'],
  ['bs_line_forecast', 'Prévisionnel bilan'],
  ['forecast_', 'Hypothèses de prévision'],
  ['pl_adjustment', 'Ajustements'],
  ['custom_report', 'Rapports sur-mesure'],
  ['governance_journals', 'Journal & décisions'],
];

// Regroupe les lignes écrasées par zone délicate ({label: n}), ignore le reste.
function delicateHits(rowsByTable) {
  const hits = {};
  Object.entries(rowsByTable || {}).forEach(([table, n]) => {
    const m = DELICATE_TABLES.find(([prefix]) => table.startsWith(prefix));
    if (m) hits[m[1]] = (hits[m[1]] || 0) + n;
  });
  return hits;
}

// Devise figée sur CHF : le formatage des montants est en CHF dans toute l'app
// (utils/format.js). Un sélecteur multi-devise serait un réglage mort.
// Première année d'analyse par défaut = année en cours − 1 (dernier exercice clos).
const DEFAULT_FIRST_YEAR = new Date().getFullYear() - 1;

/**
 * Réglages tenant — paramètres clés de l'entreprise. Rendu en onglets du hub
 * Settings (`SettingsHubPage`). Sections indépendantes, sélectionnables via la
 * prop `sections` :
 *  - company  : devise + première année d'analyse (saisie « une fois ») + langue UI
 *  - planning : horizon et niveau de planification
 *  - vat      : régime TVA (méthode, base, périodicité, décalage, taux par année)
 *  - backup   : sauvegarde complète (JSON)
 *  - restore · rename · delete : restaurer, renommer, supprimer le client
 *    (Administration › Données clients, exploitant seul)
 * `embedded` : rendu sans PageShell propre (le titre est porté par le hub).
 * `targetPicker` (Administration seulement) : les opérations sensibles exigent
 * la DÉSIGNATION explicite d'un client — aucun contexte hérité, sélecteur vide
 * par défaut (incident 2026-08-18 : sauvegarde chargée sur le mauvais client).
 */
export default function TenantSettingsPage({
  activeTenant, onTenantPatched, onRenameTenant, onDeleteTenant, addToast,
  embedded = false, sections = null, targetPicker = false, pickedTarget, onTargetGone,
}) {
  const { t } = useTranslation();
  const show = (s) => !sections || sections.includes(s);
  // Désignation explicite : liste des clients + cible choisie (vide par défaut).
  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState('');
  useEffect(() => {
    if (!targetPicker) return;
    listAdminTenants().then((res) => { if (res.data) setTargets(res.data); });
  }, [targetPicker]);
  // Client sur lequel portent les opérations sensibles : la cible désignée en
  // mode Administration, le client actif partout ailleurs (Settings du client).
  // `pickedTarget` : la cible désignée par la PAGE, en tête de section
  // (Administration › Données clients, 2026-10-11) ; la carte ne porte alors
  // pas son propre sélecteur.
  const target = pickedTarget !== undefined ? pickedTarget
    : targetPicker ? (targets.find((x) => x.id === targetId) || null)
    : activeTenant;
  const [yearDraft, setYearDraft] = useState(
    activeTenant?.first_analysis_year
      ? String(activeTenant.first_analysis_year)
      : String(DEFAULT_FIRST_YEAR)
  );
  const [yearUnlocked, setYearUnlocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  // Restauration : dump JSON chargé + sélection de modules (décochés par défaut
  // pour Paramètres entreprise, cochés pour les modules de données).
  const [restoreFile, setRestoreFile] = useState(null); // { name, dump }
  const [restoreSel, setRestoreSel] = useState({});
  const [restoring, setRestoring] = useState(false);
  // Constat renvoyé par le serveur quand la restauration détruirait des données
  // existantes (ou vient d'un autre client) : on le montre AVANT d'écrire.
  const [restorePreflight, setRestorePreflight] = useState(null);
  // Changer de cible abandonne la restauration préparée sur la précédente.
  const targetKey = target ? target.id : '';
  useEffect(() => { setRestoreFile(null); setRestorePreflight(null); }, [targetKey]);
  // Fenêtre de confirmation (pop-up) : « confirmation » à écrire seulement
  // quand le client ou l'identifiant (slug) diffèrent de la sauvegarde.
  const [ackOpen, setAckOpen] = useState(false);
  const [ackWord, setAckWord] = useState('');

  const vatMethod = activeTenant?.vat_method || 'effective';

  useEffect(() => {
    setYearDraft(
      activeTenant?.first_analysis_year
        ? String(activeTenant.first_analysis_year)
        : String(DEFAULT_FIRST_YEAR)
    );
    setYearUnlocked(false);
  }, [activeTenant?.id]);

  if (!activeTenant) {
    const empty = <p className="settings-empty">{t('settings:empty')}</p>;
    return embedded ? empty : <PageShell framed title={t('settings:title')} timeless>{empty}</PageShell>;
  }

  const firstYear = activeTenant.first_analysis_year || null;
  const yearLocked = firstYear != null && !yearUnlocked;

  async function patch(fields, okMsg) {
    setSaving(true);
    try {
      const res = await updateTenant(activeTenant.id, fields);
      if (res.data) {
        onTenantPatched && onTenantPatched(res.data);
        addToast && addToast(okMsg);
        return true;
      }
      addToast && addToast(res.error || t('common:errors.generic'), 'error');
    } catch (err) {
      addToast && addToast(t('common:errors.withMessage', { message: err.message }), 'error');
    } finally {
      setSaving(false);
    }
    return false;
  }

  // Sélection de méthode : bascule la périodicité SEULEMENT si l'actuelle est
  // incompatible (effective↔trimestrielle, tdfn↔semestrielle). Changement
  // déclenché par le clic de l'utilisateur, jamais en douce au chargement.
  function selectMethod(m) {
    const p = activeTenant?.vat_periodicity || 'quarterly';
    const incompatible = (m === 'tdfn' && p === 'quarterly') || (m === 'effective' && p === 'semiannual');
    const payload = { vat_method: m };
    if (incompatible) payload.vat_periodicity = m === 'tdfn' ? 'semiannual' : 'quarterly';
    patch(payload, t('settings:vat.methodSaved'));
  }

  async function handleSaveYear() {
    const y = parseInt(yearDraft, 10);
    if (!Number.isFinite(y) || y < 2000 || y > 2100) {
      addToast && addToast(t('common:errors.invalidYear'), 'error');
      return;
    }
    const ok = await patch(
      { first_analysis_year: y },
      t('settings:company.firstYearSaved', { year: y }),
    );
    if (ok) setYearUnlocked(false);
  }

  const compareYear = Number.isFinite(parseInt(yearDraft, 10)) ? parseInt(yearDraft, 10) - 1 : null;

  // ── Sauvegarde JSON (dump complet) + restauration par module ──────────────
  async function handleDownloadDump() {
    if (!target) return;
    const dump = await dumpTenant(target.id);
    if (!dump || !dump.tables) { addToast && addToast(t('common:errors.generic'), 'error'); return; }
    // Diagnostic serveur : des valeurs obligatoires vides feraient échouer la
    // restauration sur MySQL — averti ICI, pas après un aller-retour en prod.
    const { warnings, ...cleanDump } = dump;
    if (warnings && warnings.length) {
      addToast && addToast(
        t('settings:restore.dumpWarnings',
          'Sauvegarde téléchargée, mais des valeurs obligatoires sont vides ({{list}}) — la restauration la refusera. Corriger la donnée d’abord.',
          { list: warnings.slice(0, 5).join(', ') }),
        'error'
      );
    }
    const blob = new Blob([JSON.stringify(cleanDump, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `palestre_${target.slug || target.id}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function handleRestoreFile(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const dump = JSON.parse(reader.result);
        if (!dump || typeof dump.tables !== 'object') throw new Error('format');
        setRestoreFile({ name: f.name, dump });
        // Tout coché par défaut : une sauvegarde se restaure entière, réglages
        // de modules compris. L'identité du client, elle, n'y est jamais
        // (protégée côté serveur) — plus besoin d'une case décochée « au cas où ».
        setRestoreSel(Object.fromEntries(RESTORE_MODULES.map(([id]) => [id, true])));
      } catch (err) {
        addToast && addToast(t('settings:restore.badFile', 'Fichier de sauvegarde invalide.'), 'error');
      }
    };
    reader.readAsText(f);
  }

  async function handleRestore(confirmed = false) {
    const modules = RESTORE_MODULES.map(([id]) => id).filter((id) => restoreSel[id]);
    if (!modules.length || !restoreFile || !target) return;
    setRestoring(true);
    try {
      const res = await restoreTenant(target.id, modules, restoreFile.dump, confirmed);
      if (res && res.preflight) {
        // Rien n'a été écrit : le serveur demande une confirmation éclairée —
        // ouverte directement en fenêtre (pop-up), pas en panneau dans la page.
        setRestorePreflight(res.preflight);
        setAckWord('');
        setAckOpen(true);
      } else if (res && res.data) {
        const n = Object.values(res.data.restored || {}).reduce((s, x) => s + x, 0);
        addToast && addToast(t('settings:restore.done', '{{n}} lignes restaurées.', { n }));
        setRestoreFile(null);
        setRestorePreflight(null);
      } else {
        addToast && addToast((res && res.error) || t('common:errors.generic'), 'error');
      }
    } catch (err) {
      addToast && addToast(t('common:errors.withMessage', { message: err.message }), 'error');
    } finally {
      setRestoring(false);
    }
  }

  const content = (
    <>
      {/* ── Company ──────────────────────────────────────────────────────── */}
      {show('company') && (
      <section className="settings-card">
        <h2 className="settings-card-title">{t('settings:company.title')}</h2>

        <div className="settings-row">
          <span className="settings-lbl">{t('settings:company.currency')}</span>
          <span className="settings-readonly">CHF</span>
        </div>

        {/* ⚑ La langue a été DÉPLACÉE dans le pied du rail (préférence par
            utilisateur, `palestre_locale` par navigateur) — elle n'a pas sa place
            dans Paramètres, réservé à l'admin du tenant. Voir Sidebar (OD 2026-08). */}

        <div className="settings-row">
          <label className="settings-lbl" htmlFor="settings-year">
            {t('settings:company.firstYear')}
            <span className="settings-hint">{t('settings:company.firstYearHint')}</span>
          </label>
          {yearLocked ? (
            <div className="settings-locked">
              <span className="settings-locked-val">{firstYear}</span>
              <button
                type="button"
                className="settings-btn settings-btn--ghost"
                onClick={() => setYearUnlocked(true)}
                title={t('settings:company.unlockTitle')}
              >{t('common:actions.unlock')}</button>
            </div>
          ) : (
            <div className="settings-year-edit">
              <input
                id="settings-year"
                type="number"
                className="settings-input settings-input--year"
                min="2000"
                max="2100"
                step="1"
                placeholder="2025"
                value={yearDraft}
                onChange={e => setYearDraft(e.target.value)}
                disabled={saving}
              />
              <button
                type="button"
                className="settings-btn settings-btn--primary"
                onClick={handleSaveYear}
                disabled={saving}
              >{t('common:actions.save')}</button>
            </div>
          )}
        </div>

        <div className="settings-row">
          <span className="settings-lbl">
            {t('settings:company.compareYear')}
            <span className="settings-hint">{t('settings:company.compareYearHint')}</span>
          </span>
          <span className="settings-readonly">{compareYear ?? t('common:dash')}</span>
        </div>
      </section>
      )}

      {/* ── Jeu de couleurs du CLIENT (2026-09-01, rouvre la charte figée du
          2026-07-23 sur décision Olivier) : trois jeux, tokens seulement. */}
      {show('company') && (
      <section className="settings-card">
        <h2 className="settings-card-title">{t('settings:theme.title', 'Jeu de couleurs')}</h2>
        <div className="settings-row">
          <span className="settings-lbl">
            {t('settings:theme.label', 'Palette de l’interface')}
            <span className="settings-hint">{t('settings:theme.hint', 'pour tous les utilisateurs de ce client')}</span>
          </span>
          <select
            className="settings-input"
            value={activeTenant.ui_theme || 'olive'}
            disabled={saving}
            onChange={(e) => patch({ ui_theme: e.target.value }, t('settings:theme.saved', 'Jeu de couleurs enregistré.'))}
          >
            <option value="olive">{t('settings:theme.olive', 'Olive (défaut)')}</option>
            <option value="ardoise">{t('settings:theme.ardoise', 'Ardoise')}</option>
            <option value="glacier">{t('settings:theme.glacier', 'Bleu-gris')}</option>
            <option value="mono">{t('settings:theme.mono', 'Monochrome (noir/blanc)')}</option>
          </select>
        </div>
      </section>
      )}

      {/* L'horizon de planification appartient à chaque version de plan depuis
          le 2026-10-11 : il se règle dans le tableau des versions. */}

      {/* ── Niveau de planification (décision Olivier, 2026-09-29) : il se
          CHOISIT ici, une fois pour le client, pour tous les écrans et tous
          les exercices ; Pro demande Finance. Les salaires suivent People. Les
          écrans ne portent plus de bascule, la bande de contexte le rappelle. */}
      {show('planning') && (() => {
        const mods = activeTenant.enabled_modules || [];
        const fin = mods.includes('financials');
        const sop = mods.includes('scenarii');
        const pack = fin && sop ? t('settings:level.both', 'Ventes & Ops et Finance')
          : fin ? t('settings:level.finOnly', 'Finance seul')
          : sop ? t('settings:level.sopOnly', 'Ventes & Ops seul') : '—';
        /* LES SALAIRES NE SE CHOISISSENT PLUS ICI (2026-09-30) : en Pro, le
           compte de résultat fait foi ; en Manuel, ils se saisissent et
           s'alignent sur People dans Salaires prévisionnels. */
        return (
          <section className="settings-card">
            <h2 className="settings-card-title">{t('settings:level.title', 'Niveau de planification')}</h2>
            <div className="settings-row">
              <span className="settings-lbl">
                {t('settings:level.label', 'Niveau')}
                <span className="settings-hint">{pack}</span>
              </span>
              <SegmentedToggle compact
                value={activeTenant.planning_level === 'pro' && fin ? 'pro' : 'manual'}
                onChange={(v) => patch({ planning_level: v }, t('settings:level.saved', 'Niveau de planification enregistré.'))}
                options={[
                  { value: 'manual', label: t('settings:level.manual', 'Manuel') },
                  { value: 'pro', label: <span className="lk-lockopt">{t('settings:level.pro', 'Pro (Finance)')}<Lock size={11} aria-hidden="true" /></span>,
                    disabled: !fin || saving, title: fin ? undefined : t('settings:level.needsFin', 'Demande le module Finance') },
                ]} />
            </div>
          </section>
        );
      })()}

      {/* ── TVA (régime légal de l'entreprise, partagé par les scénarios cash) ─
          ⚑ MARQUEUR — Section TVA + clés i18n `vat.*` refondues par Olivier le
          2026-08-03 (cartes méthode, toggles, périodicité semestrielle · PR #113),
          par-dessus l'i18n de Victor. Coordonner avant de retoucher cette zone. */}
      {show('vat') && (
      <section className="settings-card">
        <h2 className="settings-card-title">{t('settings:vat.title')}</h2>

        {/* Méthode de décompte — cartes sélectionnables (Forfaitaire retiré :
            hors périmètre PME ; l'effective couvre les cas fondation au besoin). */}
        <div className="settings-vat-methods-lbl">{t('settings:vat.methodTitle')}</div>
        <div className="settings-vat-methods">
          {[
            { id: 'effective', name: t('settings:vat.methodEffectiveShort'), sub: t('settings:vat.methodEffectiveSub') },
            { id: 'tdfn', name: t('settings:vat.methodTdfnShort'), sub: t('settings:vat.methodTdfnSub') },
          ].map(m => (
            <button
              key={m.id} type="button"
              className={`settings-vat-method${vatMethod === m.id ? ' is-active' : ''}`}
              onClick={() => selectMethod(m.id)} disabled={saving}
            >
              <span className="settings-vat-method__name">{m.name}</span>
              <span className="settings-vat-method__sub">{m.sub}</span>
            </button>
          ))}
        </div>

        {/* Base · Périodicité · Décalage — toggles segmentés. Périodicité
            contextuelle : Trim. pour l'effective, Sem. pour le TDFN. */}
        <div className="settings-vat-controls">
          <div className="settings-vat-ctrl">
            <div className="settings-vat-ctrl__lbl">{t('settings:vat.basis')}</div>
            <div className="settings-seg">
              {[['agreed', t('settings:vat.basisAgreedShort')], ['received', t('settings:vat.basisReceivedShort')]].map(([v, l]) => (
                <button key={v} type="button"
                  className={`settings-seg__b${(activeTenant?.vat_basis || 'agreed') === v ? ' is-active' : ''}`}
                  onClick={() => patch({ vat_basis: v }, t('settings:vat.basisSaved'))} disabled={saving}>{l}</button>
              ))}
            </div>
          </div>

          <div className="settings-vat-ctrl">
            <div className="settings-vat-ctrl__lbl">{t('settings:vat.periodicity')}</div>
            <div className="settings-seg">
              {[
                ['quarterly', t('settings:vat.periodQuarterlyShort'), vatMethod === 'tdfn'],
                ['semiannual', t('settings:vat.periodSemiannualShort'), vatMethod === 'effective'],
                ['annual', t('settings:vat.periodAnnualShort'), false],
              ].map(([v, l, off]) => (
                <button key={v} type="button"
                  className={`settings-seg__b${(activeTenant?.vat_periodicity || 'quarterly') === v ? ' is-active' : ''}`}
                  onClick={() => patch({ vat_periodicity: v }, t('settings:vat.periodSaved'))}
                  disabled={saving || off}>{l}</button>
              ))}
            </div>
          </div>

          <div className="settings-vat-ctrl">
            <div className="settings-vat-ctrl__lbl">{t('settings:vat.lag')}</div>
            <div className="settings-seg">
              {[[1, t('settings:vat.lag30')], [2, t('settings:vat.lag60')]].map(([v, l]) => (
                <button key={v} type="button"
                  className={`settings-seg__b${(activeTenant?.vat_payment_lag ?? 2) === v ? ' is-active' : ''}`}
                  onClick={() => patch({ vat_payment_lag: v }, t('settings:vat.lagSaved'))} disabled={saving}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="settings-row settings-row--block">
          <VatRatesGrid
            tenantId={activeTenant.id}
            method={vatMethod}
            firstAnalysisYear={activeTenant.first_analysis_year}
            addToast={addToast}
          />
        </div>
      </section>
      )}

      {/* ── Désignation du client visé — une seule ligne en tête, elle gouverne
             toutes les cards en dessous (renommer, sauvegarder, restaurer,
             supprimer). Vide par défaut : aucun contexte hérité. */}
      {targetPicker && (
      <div className="settings-target-bar">
        <span className="settings-target-lbl">{t('settings:restore.targetTitle', 'Client cible')}</span>
        <select
          className="settings-target-select"
          value={targetId}
          onChange={(e) => { setTargetId(e.target.value); setRestoreFile(null); setRestorePreflight(null); }}
        >
          <option value="">{t('settings:restore.targetPlaceholder', '— Désigner le client —')}</option>
          {targets.map((tn) => (
            <option key={tn.id} value={tn.id}>{tn.name}</option>
          ))}
        </select>
      </div>
      )}

      {/* ── Sauvegarde, restauration, renommage, suppression : dans cet ordre
             (Administration › Données clients, 2026-10-11). ──────────────── */}
      {show('backup') && (
      <section className="settings-card">
        <h2 className="settings-card-title">{t('settings:restore.dumpTitle', 'Sauvegarde complète')}</h2>
        <div className="settings-danger-row">
          <div>
            <div className="settings-danger-lbl">{t('settings:restore.dumpLbl', 'Sauvegarde complète (JSON)')}</div>
            <div className="settings-danger-desc">{t('settings:restore.dumpDesc', 'Toutes les tables du client, fidèle à 100% — le véhicule de migration entre environnements.')}</div>
          </div>
          <button type="button" className="settings-btn" onClick={handleDownloadDump} disabled={!target}>
            {t('settings:restore.download', 'Télécharger')}
          </button>
        </div>
      </section>
      )}

      {show('restore') && (
      <section className="settings-card settings-card--alert">
        <h2 className="settings-card-title">{t('settings:restore.title', 'Restauration des données')}</h2>
        <div className="settings-danger-row">
          <div>
            <div className="settings-danger-lbl">{t('settings:restore.restoreLbl', 'Restaurer une sauvegarde')}</div>
            <div className="settings-danger-desc">{t('settings:restore.restoreDesc', 'Charge un fichier JSON puis choisis les modules — leurs données actuelles seront REMPLACÉES.')}</div>
          </div>
          <label className={`settings-btn${!target ? ' is-disabled' : ''}`} style={{ cursor: target ? 'pointer' : 'not-allowed' }}>
            {t('settings:restore.chooseFile', 'Choisir un fichier…')}
            <input type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={handleRestoreFile} disabled={!target} />
          </label>
        </div>
        {restoreFile && target && (
          <div className="settings-restore-panel">
            <div className="settings-danger-desc">{restoreFile.name} — {t('settings:restore.pick', 'modules à restaurer sur')} <strong>{target.name}</strong> :</div>
            {RESTORE_MODULES.map(([id, label]) => (
              <label key={id} className="settings-restore-line">
                <input type="checkbox" checked={!!restoreSel[id]}
                  onChange={(e) => setRestoreSel((m) => ({ ...m, [id]: e.target.checked }))} />
                <span>{label}</span>
              </label>
            ))}
            <div className="settings-restore-actions">
              <button type="button" className="settings-btn settings-btn--ghost" onClick={() => { setRestoreFile(null); setRestorePreflight(null); }} disabled={restoring}>
                {t('common:actions.cancel', 'Annuler')}
              </button>
              <button type="button" className="settings-btn settings-btn--danger" onClick={() => handleRestore(false)}
                disabled={restoring || !RESTORE_MODULES.some(([id]) => restoreSel[id])}>
                {restoring ? t('settings:restore.running', 'Restauration…') : t('settings:restore.confirm', 'Restaurer (remplace les modules cochés)')}
              </button>
            </div>
          </div>
        )}


        {/* Fenêtres de confirmation (pop-up) — le serveur a refusé une première
            fois et renvoyé ce qu'il s'apprêtait à détruire ; rien n'est écrit.
            Deux niveaux :
            - client OU slug différents → ATTENTION + écrire « confirmation » ;
            - même client → simple OK, avec le volume et les zones délicates
              (données saisies à la main) mis en évidence. */}
        {ackOpen && restorePreflight && (() => {
          const pre = restorePreflight;
          const n = pre.total_detruit;
          const slugDiff = Boolean(pre.source?.slug && pre.cible?.slug && pre.source.slug !== pre.cible.slug);
          const cross = !pre.meme_client || slugDiff;
          const delicate = delicateHits(pre.lignes_detruites);
          const delicateTotal = Object.values(delicate).reduce((s, x) => s + x, 0);
          const wordOk = !cross || ackWord.trim().toLowerCase() === 'confirmation';
          const close = () => { setAckOpen(false); setRestorePreflight(null); };
          return (
            <div className="settings-modal-overlay">
              <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
                <div className="settings-modal-header">
                  <span className={`settings-modal-title${cross ? ' settings-ack-title--danger' : ''}`}>
                    {cross
                      ? t('settings:restore.ackTitleCross', 'ATTENTION')
                      : t('settings:restore.ackTitleSame', 'Confirmer la restauration')}
                  </span>
                  <button className="settings-modal-close" onClick={close}>✕</button>
                </div>
                <div className="settings-modal-body">
                  {/* L'encadré d'alerte porte sur la DIFFÉRENCE de client (le
                      vrai danger) ; le volume remplacé, obligatoire, reste en
                      texte simple. */}
                  {cross && (
                    <div className="settings-modal-warn">
                    <ul className="settings-restore-alerts">
                      {!pre.meme_client && (
                        <li><Trans i18nKey="settings:restore.alertName"
                          values={{ src: pre.source?.nom || '?', dst: pre.cible?.nom }}
                          components={{ b: <strong /> }}
                          defaults="Le nom du client de la sauvegarde (« <b>{{src}}</b> ») diffère du client cible (« <b>{{dst}}</b> »)." /></li>
                      )}
                      {slugDiff && (
                        <li><Trans i18nKey="settings:restore.alertSlug"
                          values={{ src: pre.source.slug, dst: pre.cible.slug }}
                          components={{ b: <strong /> }}
                          defaults="L’identifiant (slug) diffère : « <b>{{src}}</b> » dans la sauvegarde, « <b>{{dst}}</b> » pour le client cible." /></li>
                      )}
                    </ul>
                    </div>
                  )}
                  <p className="settings-ack-intro">
                    <Trans i18nKey="settings:restore.ackIntro"
                      values={{ n, x: pre.total_charge ?? 0, dst: pre.cible?.nom, src: pre.source?.nom || '?' }}
                      components={{ b: <strong /> }}
                      defaults="La sauvegarde de « <b>{{src}}</b> » va charger <b>{{x}}</b> lignes dans « <b>{{dst}}</b> » ; ses {{n}} lignes actuelles seront supprimées. Cette opération ne peut pas être annulée." />
                  </p>
                  {delicateTotal > 0 && (
                    <div className="settings-ack-delicate">
                      <div className="settings-ack-delicate__lbl">
                        {t('settings:restore.delicateLbl', 'Données saisies à la main touchées (coûteuses à reconstituer) :')}
                      </div>
                      <ul>
                        {Object.entries(delicate).sort((a, b) => b[1] - a[1]).map(([lbl, k]) => (
                          <li key={lbl}>{lbl} — {k} {t('settings:restore.rows', 'lignes')}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {n > 0 && (
                    <details className="settings-restore-detail">
                      <summary>{t('settings:restore.detailToggle', 'Détail par table')}</summary>
                      <ul>
                        {Object.entries(pre.lignes_detruites)
                          .sort((a, b) => b[1] - a[1])
                          .map(([tbl, k]) => <li key={tbl}><code>{tbl}</code> — {k}</li>)}
                      </ul>
                    </details>
                  )}
                  {cross && (
                    <label className="settings-restore-type">
                      {t('settings:restore.ackWordLabel', 'Pour confirmer malgré la différence, écris « confirmation » :')}
                      <input type="text" value={ackWord} autoComplete="off"
                        placeholder={t('settings:restore.ackWordPlaceholder', 'confirmation')}
                        onChange={(e) => setAckWord(e.target.value)} />
                    </label>
                  )}
                  <div className="settings-restore-actions">
                    <button type="button" className="settings-btn settings-btn--ghost"
                      onClick={close} disabled={restoring}>
                      {t('common:actions.cancel', 'Annuler')}
                    </button>
                    <button type="button" className="settings-btn settings-btn--danger"
                      disabled={restoring || !wordOk}
                      onClick={() => { setAckOpen(false); handleRestore(true); }}>
                      {restoring
                        ? t('settings:restore.running', 'Restauration…')
                        : cross
                          ? t('settings:restore.ackGoCross', 'Restaurer malgré tout')
                          : t('settings:restore.ackGoSame', 'OK, restaurer ({{x}} lignes)', { x: pre.total_charge ?? 0 })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </section>
      )}

      {/* ── Renommer — Administration uniquement (identité contractuelle) ─── */}
      {show('rename') && (
      <section className="settings-card">
        <h2 className="settings-card-title">{t('settings:rename.title')}</h2>
        <div className="settings-danger-row">
          <div>
            <div className="settings-danger-lbl">{target?.name || t('common:dash')}</div>
            <div className="settings-danger-desc">{t('settings:rename.desc')}</div>
          </div>
          <button
            type="button"
            className="settings-btn settings-btn--ghost"
            disabled={!target}
            onClick={() => target && onRenameTenant && onRenameTenant(target.id)}
          >{t('settings:rename.button')}</button>
        </div>
      </section>
      )}

      {show('delete') && (
      <section className="settings-card settings-card--danger">
        <h2 className="settings-card-title">{t('settings:danger.title')}</h2>
        {/* Le client visé doit être lisible AVANT d'ouvrir la modale : cet écran
            vit dans Administration, où le contexte est la plateforme entière.
            En mode désignation (targetPicker), c'est la cible DÉSIGNÉE — jamais
            un contexte hérité. */}
        <p className="settings-danger-target">
          {t('settings:danger.selectedClient')}{' '}
          <strong>{target?.name || t('common:dash')}</strong>
        </p>
        <div className="settings-danger-row">
          <div>
            <div className="settings-danger-lbl">{t('settings:danger.deleteAccount')}</div>
            <div className="settings-danger-desc">{t('settings:danger.deleteDesc')}</div>
          </div>
          <button
            type="button"
            className="settings-btn settings-btn--danger"
            disabled={!target}
            onClick={() => setShowDelete(true)}
          >{t('settings:danger.deleteButton', { name: target?.name })}</button>
        </div>
      </section>
      )}

      {showDelete && target && (
        <DeleteTenantModal
          tenant={target}
          addToast={addToast}
          onClose={() => setShowDelete(false)}
          onConfirm={async () => {
            await (onDeleteTenant && onDeleteTenant(target.id));
            setShowDelete(false);
            // La cible désignée vient d'être supprimée : on repart d'un
            // sélecteur vide, avec une liste rafraîchie.
            if (onTargetGone) onTargetGone();
            if (targetPicker) {
              setTargetId('');
              listAdminTenants().then((res) => { if (res.data) setTargets(res.data); });
            }
          }}
        />
      )}
    </>
  );

  return embedded ? content : <PageShell framed title={t('settings:title')} timeless>{content}</PageShell>;
}
